interface ParsedContact {
  firstName?: string;
  lastName?: string;
  nicknames?: string[];
  email?: string;
  phone?: string;
}

export function parseVCard(text: string): ParsedContact[] {
  const contacts: ParsedContact[] = [];
  const cards = text.split('BEGIN:VCARD').slice(1);

  for (const card of cards) {
    const contact: ParsedContact = {};
    const lines = unfoldVCardLines(card);

    for (const line of lines) {
      const [propPart, ...valueParts] = line.split(':');
      const value = valueParts.join(':').trim();
      const propName = propPart.split(';')[0].toUpperCase();

      if (propName === 'N' && value) {
        const parts = value.split(';');
        contact.lastName = parts[0] || undefined;
        contact.firstName = parts[1] || undefined;
      } else if (propName === 'FN' && !contact.firstName && !contact.lastName) {
        const parts = value.split(' ');
        contact.firstName = parts[0] || undefined;
        contact.lastName = parts.slice(1).join(' ') || undefined;
      } else if (propName === 'EMAIL' && value) {
        contact.email = contact.email || value;
      } else if (propName === 'TEL' && value) {
        contact.phone = contact.phone || value;
      } else if (propName === 'NICKNAME' && value) {
        contact.nicknames = value.split(',').map((n) => n.trim()).filter(Boolean);
      }
    }

    if (contact.firstName || contact.lastName || contact.email) {
      contacts.push(contact);
    }
  }

  return contacts;
}

function unfoldVCardLines(text: string): string[] {
  return text.replace(/\r\n[ \t]/g, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter((l) => l.trim());
}

const HEADER_MAP: Record<string, keyof ParsedContact> = {
  'first name': 'firstName',
  'first_name': 'firstName',
  'firstname': 'firstName',
  'given name': 'firstName',
  'given_name': 'firstName',
  'last name': 'lastName',
  'last_name': 'lastName',
  'lastname': 'lastName',
  'family name': 'lastName',
  'family_name': 'lastName',
  'e-mail 1 - value': 'email',
  'e-mail address': 'email',
  'email': 'email',
  'email address': 'email',
  'email_address': 'email',
  'phone 1 - value': 'phone',
  'phone': 'phone',
  'phone_number': 'phone',
  'mobile phone': 'phone',
  'nickname': 'nicknames',
};

export function parseCSV(text: string): ParsedContact[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];

  const headers = parseCSVLine(lines[0]);
  const fieldMap: Array<keyof ParsedContact | null> = headers.map((h) => {
    const normalized = h.toLowerCase().trim();
    return HEADER_MAP[normalized] ?? null;
  });

  const contacts: ParsedContact[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const contact: ParsedContact = {};
    for (let j = 0; j < fieldMap.length; j++) {
      const field = fieldMap[j];
      const val = values[j]?.trim();
      if (!field || !val) continue;
      if (field === 'nicknames') {
        contact.nicknames = val.split(',').map((n) => n.trim()).filter(Boolean);
      } else {
        (contact as Record<string, string>)[field] = val;
      }
    }
    if (contact.firstName || contact.lastName || contact.email) {
      contacts.push(contact);
    }
  }

  return contacts;
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}
